<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    id="OffcanvasPromotionForm"
    ref="offcanvas"
    data-bs-backdrop="static"
    style="--bs-offcanvas-width: 760px;"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE' ? $t('_promotion.Create Promotion') : $t('_promotion.Edit Promotion') }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div class="offcanvas-body p-0" v-else>
      <!-- Promotion Information -->
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />{{ $t('_promotion.Promotion Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-12">
            <label class="form-label">{{ $t('_promotion.Promotion Type') }} <span class="text-danger">*</span></label>
            <select v-model="form.type" class="form-select form-select-sm">
              <option v-for="t in typeOptions" :key="t.value" :value="t.value">{{ $t('_promotion.' + t.label) }}</option>
            </select>
            <div class="form-text" style="font-size: 11px;">{{ typeHint }}</div>
          </div>
          <div class="col-md-8">
            <label for="name" class="form-label">{{ $t('Name') }} <span class="text-danger">*</span></label>
            <input
              id="name" type="text"
              v-model.trim="v$.form.name.$model"
              class="form-control form-control-sm"
              :class="v$.form.name.$error || serverError('name') ? 'is-invalid' : ''"
              :placeholder="$t('_promotion.Enter promotion name') + '...'"
            >
            <div v-for="error of v$.form.name.$errors" class="invalid-feedback" :key="error.$uid">{{ error.$message }}</div>
            <div v-for="error of serverErrors('name')" class="invalid-feedback" :key="error">{{ $t(error) }}</div>
          </div>
          <div class="col-md-4">
            <label for="priority" class="form-label">
              {{ $t('Priority') }}
              <popper :placement="'top'" :hover="true" :arrow="true" class="popper-default border-0 m-0 d-inline-block">
                <i class="fa-solid fa-circle-question text-muted" />
                <template #content>
                  <div>{{ $t('_promotion.Highest priority wins — 1 bill uses 1 promotion') }}</div>
                </template>
              </popper>
            </label>
            <input id="priority" type="number" min="0" step="1" v-model.number="form.priority" class="form-control form-control-sm">
          </div>
          <div class="col-md-6">
            <label for="description" class="form-label">{{ $t('_promotion.Description') }}</label>
            <vue-editor
              v-model="v$.form.description.$model"
              :editor-toolbar="editor.toolbar"
              :placeholder="$t('Enter description') + '...'"
              :class="v$.form.description.$error? 'is-invalid': ''"
            />
            <!-- <textarea id="description" rows="2" v-model.trim="form.description" class="form-control form-control-sm"></textarea> -->
          </div>
          <div class="col-md-3">
            <label class="form-label">{{ $t('_promotion.Start Date') }}</label>
            <date-range-picker
              v-model="form.startRange" :date-range="form.startRange"
              :single-date-picker="true" :ranges="false" :auto-apply="true" :show-dropdowns="true"
              :locale-data="dateRangeLocale" :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate">{{ formatDate(picker.startDate) }}</span>
                <span v-else class="text-muted">{{ $t('Select date') }}</span>
              </template>
            </date-range-picker>
          </div>
          <div class="col-md-3">
            <label class="form-label">{{ $t('_promotion.End Date') }}</label>
            <date-range-picker
              v-model="form.endRange" :date-range="form.endRange"
              :single-date-picker="true" :ranges="false" :auto-apply="true" :show-dropdowns="true"
              :locale-data="dateRangeLocale" :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate">{{ formatDate(picker.startDate) }}</span>
                <span v-else class="text-muted">{{ $t('Select date') }}</span>
              </template>
            </date-range-picker>
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check">
              <input id="is_active" type="checkbox" class="form-check-input" v-model="form.is_active">
              <label for="is_active" class="form-check-label">{{ $t('_promotion.Active') }}</label>
            </div>
          </div>
          <div class="col-md-8 d-flex align-items-end">
            <div class="form-check">
              <input id="require_member" type="checkbox" class="form-check-input" v-model="form.condition_require_member">
              <label for="require_member" class="form-check-label">{{ $t('_promotion.Require member') }}</label>
            </div>
          </div>
          <template v-if="form.condition_require_member">
            <div class="col-md-4">
              <label class="form-label">{{ $t('_promotion.Customer Group') }}</label>
              <select v-model="form.condition_customer_group_id" class="form-select form-select-sm">
                <option value="">{{ $t('_promotion.All members') }}</option>
                <option v-for="g in customerGroupOptions" :key="g.id" :value="g.id">{{ $t(g.text) }}</option>
              </select>
              <small class="text-muted">{{ $t('_promotion.Limit to a specific customer group') }}</small>
            </div>
            <div class="col-md-4">
              <label class="form-label">{{ $t('_promotion.Quota per user per day') }}</label>
              <input type="number" min="0" step="1" v-model.number="form.quota_per_user_per_day" class="form-control form-control-sm" placeholder="0">
              <small class="text-muted">{{ $t('_promotion.0 = unlimited; shared across all products') }}</small>
            </div>
            <div class="col-md-4">
              <label class="form-label">{{ $t('_promotion.Daily limit (total)') }}</label>
              <input type="number" min="0" step="1" v-model.number="form.daily_limit_total" class="form-control form-control-sm" placeholder="0">
              <small class="text-muted">{{ $t('_promotion.0 = unlimited; whole shop per day') }}</small>
            </div>
          </template>
        </div>
      </div>

      <!-- Calculation rules (tiered_reward only) -->
      <div class="border-bottom mb-3 px-3 py-2" v-if="form.type === 'tiered_reward'">
        <i class="fa-solid fa-sliders me-2" />{{ $t('_promotion.Calculation') }}
      </div>
      <div class="px-3" v-if="form.type === 'tiered_reward'">
        <div class="row g-3 py-2">
          <div class="col-md-6">
            <label class="form-label">{{ $t('_promotion.Tier Mode') }} <span class="text-danger">*</span></label>
            <select v-model="form.tier_mode" class="form-select form-select-sm">
              <option value="single">{{ $t('_promotion.tier_mode_single') }}</option>
              <option value="cumulative">{{ $t('_promotion.tier_mode_cumulative') }}</option>
            </select>
          </div>
          <div class="col-md-6 d-flex align-items-end gap-3">
            <div class="form-check">
              <input id="base_after_discount" type="checkbox" class="form-check-input" v-model="form.base_after_discount">
              <label for="base_after_discount" class="form-check-label">{{ $t('_promotion.Count after discount') }}</label>
            </div>
            <div class="form-check">
              <input id="base_include_vat" type="checkbox" class="form-check-input" v-model="form.base_include_vat">
              <label for="base_include_vat" class="form-check-label">{{ $t('_promotion.Include VAT') }}</label>
            </div>
          </div>
          <div class="col-12">
            <div class="form-check">
              <input id="show_reward_stock_early" type="checkbox" class="form-check-input" v-model="form.show_reward_stock_early">
              <label for="show_reward_stock_early" class="form-check-label">{{ $t('_promotion.Warn out of stock early') }}</label>
              <div class="form-text" style="font-size: 11px;">{{ $t('_promotion.Warn out of stock early hint') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tiers -->
      <div class="border-bottom mb-3 px-3 py-2 d-flex justify-content-between align-items-center">
        <span><i class="fa-solid fa-layer-group me-2" />{{ form.type === 'item_discount' ? $t('_promotion.Discount Rules') : $t('_promotion.Tiers') }}</span>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="addTier">
          <i class="fa-solid fa-circle-plus me-1" />{{ $t('Add') }}
        </button>
      </div>
      <div class="px-3 pb-3">
        <draggable
          v-model="form.items"
          item-key="_uid"
          handle=".drag-handle"
          :animation="200"
          ghost-class="drag-ghost"
        >
          <!--
            ⚠️ slot #item ของ vuedraggable รับลูกได้ "ตัวเดียว" — คอมเมนต์ก็นับเป็น node
            ห้ามวางอะไรคั่นระหว่าง <template #item> กับ <div> ข้างใน (Error: Item slot must have only one child)

            ⚠️ การ์ดข้างในใช้ @change เท่านั้น ห้ามใส่ @input
            <select> ยิง input ก่อน change — ถ้าไปแก้ state ตอน input จะทำให้ Vue re-render
            คั่นกลาง แล้ว patch ค่า value ของ select กลับเป็นค่าเดิมใน model (ยังไม่ทันอัปเดต
            เพราะ v-model ของ select รอ change) พอ change ยิงจริงค่ากลายเป็นของเดิมไปแล้ว
            → เลือกครั้งแรกไม่ติด ต้องกดซ้ำอีกรอบ
          -->
          <template #item="{ element: tier, index: idx }">
            <div class="border rounded p-2 mb-2" @change="tier._new = false">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <span class="fw-semibold d-inline-flex align-items-center" style="font-size: 12px;">
              <span class="drag-handle d-inline-flex align-items-center justify-content-center me-1"
                style="width: 22px; height: 22px; font-size: 12px; cursor: grab; touch-action: none;"
                :title="$t('Drag to reorder')">
                <i class="fa-solid fa-grip-vertical text-muted" />
              </span>
              {{ form.type === 'item_discount' ? $t('_promotion.Rule') : $t('_promotion.Tier') }} {{ idx + 1 }}
              <!--
                สรุปกฎเป็นภาษาคนต่อท้ายหัวการ์ด — โปรหนึ่งตัวมีได้หลายกฎ ถ้าไม่มีสรุป
                ต้องไล่กางอ่านทีละใบถึงจะรู้ว่าใบไหนคือใบไหน
              -->
              <span v-if="tierSummary(tier)" class="badge bg-primary-subtle text-primary-emphasis ms-2 fw-normal">
                {{ tierSummary(tier) }}
              </span>
            </span>
            <button type="button" class="btn btn-sm btn-outline-danger btn-icon" @click="form.items.splice(idx, 1)">
              <i class="fa-solid fa-trash-can" />
            </button>
          </div>
          <div class="row g-2 align-items-end">
            <!-- Condition (threshold) — tiered_reward only -->
            <template v-if="form.type === 'tiered_reward'">
            <div class="col-md-4">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('_promotion.Condition') }}</label>
              <select v-model="tier.condition_type" class="form-select form-select-sm">
                <option value="spend">{{ $t('_promotion.condition_spend') }}</option>
                <option value="quantity">{{ $t('_promotion.condition_quantity') }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label mb-1" style="font-size: 12px;">
                {{ tier.condition_type === 'quantity' ? $t('_promotion.Buy Quantity') : $t('_promotion.Minimum Spend') }}
              </label>
              <!-- หน่วยติดท้ายช่องเหมือนช่องเงินอื่น — "300" เฉย ๆ อ่านไม่ออกว่าบาทหรือชิ้น -->
              <div class="input-group input-group-sm">
                <input
                  v-if="tier.condition_type === 'quantity'"
                  type="number" min="0" step="1" v-model.number="tier.buy_quantity" class="form-control form-control-sm"
                >
                <input
                  v-else
                  type="number" min="0" step="0.01" v-model.number="tier.minimum_spend" class="form-control form-control-sm"
                  :class="tierError(idx, 'minimum_spend') ? 'is-invalid' : ''"
                >
                <span class="input-group-text">{{ tier.condition_type === 'quantity' ? $t('_promotion.unit_piece') : '฿' }}</span>
              </div>
              <div v-if="tierError(idx, tier.condition_type === 'quantity' ? 'buy_quantity' : 'minimum_spend')" class="text-danger" style="font-size: 11px;">
                {{ tierError(idx, tier.condition_type === 'quantity' ? 'buy_quantity' : 'minimum_spend') }}
              </div>
            </div>
            </template>
            <!-- Scope -->
            <div class="col-md-4">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('_promotion.Scope') }}</label>
              <select v-model="tier.scope" class="form-select form-select-sm">
                <option v-if="form.type !== 'item_discount'" value="bill">{{ $t('_promotion.scope_bill') }}</option>
                <option value="category">{{ $t('_promotion.scope_category') }}</option>
                <option value="product">{{ $t('_promotion.scope_product') }}</option>
              </select>
            </div>
            <div class="col-md-12" v-if="tier.scope === 'category'">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('_promotion.Category') }}</label>
              <select2 v-model="tier.product_category_id" :options="categoryOptions" :settings="{ width: '100%', placeholder: $t('_promotion.Select category'), dropdownParent: '#OffcanvasPromotionForm' }" />
              <div v-if="tierError(idx, 'product_category_id')" class="text-danger" style="font-size: 11px;">{{ tierError(idx, 'product_category_id') }}</div>
            </div>
            <div class="col-md-12" v-if="tier.scope === 'product'">
              <label class="form-label mb-1" style="font-size: 12px;">{{ form.type === 'item_discount' ? $t('_promotion.Discounted Product') : $t('_promotion.Qualifying Product') }}</label>
              <select2 v-model="tier.product_id" :options="form.type === 'item_discount' ? discountProductOptions : productOptions" :settings="{ width: '100%', placeholder: $t('_promotion.Select product'), dropdownParent: '#OffcanvasPromotionForm' }" />
              <div v-if="tierError(idx, 'product_id')" class="text-danger" style="font-size: 11px;">{{ tierError(idx, 'product_id') }}</div>
            </div>

            <!-- Reward (tiered_reward) -->
            <template v-if="form.type === 'tiered_reward'">
            <div class="col-md-4">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('_promotion.Reward Type') }}</label>
              <select v-model="tier.action_type" class="form-select form-select-sm">
                <option value="free_item">{{ $t('_promotion.reward_free') }}</option>
                <option value="bundle_fixed_price">{{ $t('_promotion.reward_pwp') }}</option>
              </select>
            </div>
            <!--
              ชื่อสินค้ายาวกว่าตัวเลขจำนวนมาก — ให้ช่องเลือกสินค้ากินพื้นที่ ส่วน "จำนวน"
              แคบพอใส่เลขหลักเดียว ไม่งั้นได้กล่องเปล่ากว้างครึ่งจอสำหรับเลข 1
            -->
            <div class="col-md-6">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('_promotion.Reward Product') }}</label>
              <select2
                v-model="tier.reward_product_id"
                :options="productOptions"
                :settings="{ width: '100%', placeholder: $t('_promotion.Select reward product'), dropdownParent: '#OffcanvasPromotionForm' }"
              />
              <div v-if="tierError(idx, 'reward_product_id')" class="text-danger" style="font-size: 11px;">{{ tierError(idx, 'reward_product_id') }}</div>
            </div>
            <div class="col-md-2">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('_promotion.Free Qty') }}</label>
              <input type="number" min="1" step="1" v-model.number="tier.free_quantity" class="form-control form-control-sm text-center"
                :class="tierError(idx, 'free_quantity') ? 'is-invalid' : ''">
              <div v-if="tierError(idx, 'free_quantity')" class="text-danger" style="font-size: 11px;">{{ tierError(idx, 'free_quantity') }}</div>
            </div>
            <div class="col-md-4" v-if="tier.action_type === 'bundle_fixed_price'">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('_promotion.Reward Price') }}</label>
              <div class="input-group input-group-sm">
                <input type="number" min="0" step="0.01" v-model.number="tier.reward_price" class="form-control form-control-sm"
                  :class="tierError(idx, 'reward_price') ? 'is-invalid' : ''">
                <span class="input-group-text">฿</span>
              </div>
              <div v-if="tierError(idx, 'reward_price')" class="text-danger" style="font-size: 11px;">{{ tierError(idx, 'reward_price') }}</div>
            </div>
            </template>
            <!-- Discount (item_discount) -->
            <template v-else>
            <div class="col-md-4">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('_promotion.Discount Mode') }}</label>
              <select v-model="tier.discount_mode" class="form-select form-select-sm">
                <option value="fixed_price">{{ $t('_promotion.discount_fixed_price') }}</option>
                <option value="percent">{{ $t('_promotion.discount_percent') }}</option>
                <option value="amount">{{ $t('_promotion.discount_amount') }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label mb-1" style="font-size: 12px;">
                {{ tier.discount_mode === 'fixed_price' ? $t('_promotion.New Price') : tier.discount_mode === 'percent' ? $t('_promotion.Discount Percent') : $t('_promotion.Discount Amount') }}
              </label>
              <!-- หน่วยติดท้ายช่อง — ป้ายบอกโหมดอยู่ไกลตา คนกรอกมักเผลออ่านเป็นคนละหน่วย -->
              <div class="input-group input-group-sm">
                <input type="number" min="0" step="0.01" v-model.number="tier.discount_value" class="form-control form-control-sm"
                  :class="tierError(idx, 'discount_value') ? 'is-invalid' : ''">
                <span class="input-group-text">{{ tier.discount_mode === 'percent' ? '%' : '฿' }}</span>
              </div>
              <div v-if="tierError(idx, 'discount_value')" class="text-danger" style="font-size: 11px;">{{ tierError(idx, 'discount_value') }}</div>
            </div>
            </template>
            <!--
              โควต้าเป็นของไม่บังคับและร้านส่วนใหญ่ปล่อยว่าง — วางรวมแถวเดียวกับกฎหลัก
              ทำให้การ์ดดูรกและแยกไม่ออกว่าอะไรคือหัวใจของกฎ จึงคั่นออกมาเป็นบล็อกรอง
            -->
            <div class="col-12">
              <div class="border-top pt-2 mt-1 text-muted" style="font-size: 11px;">
                <i class="fa-solid fa-gauge-high me-1" />{{ $t('_promotion.Limits') }} · {{ $t('Optional') }}
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label mb-1 text-muted" style="font-size: 12px;">{{ $t('_promotion.Item quota per user per day') }}</label>
              <input type="number" min="0" step="1" v-model.number="tier.quota_per_user_per_day" class="form-control form-control-sm" :placeholder="$t('Unlimited')">
            </div>
            <div class="col-md-3">
              <label class="form-label mb-1 text-muted" style="font-size: 12px;">{{ $t('_promotion.Daily limit (this item)') }}</label>
              <input type="number" min="0" step="1" v-model.number="tier.daily_limit_total" class="form-control form-control-sm" :placeholder="$t('Unlimited')">
            </div>
          </div>
            </div>
          </template>
        </draggable>
        <div v-if="!form.items.length" class="text-center py-2" style="font-size: 13px;"
          :class="v$.form.items.$error ? 'text-danger' : 'text-muted'">
          {{ v$.form.items.$error ? $t('_promotion.Add at least one tier') : $t('_promotion.No tiers yet') }}
        </div>
      </div>
    </div>

    <div class="bg-light border-top justify-content-center modal-footer py-2" v-if="!loading">
      <ladda
        text="Save"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="type === 'CREATE' ? handleCreate() : handleUpdate()"
      />
      <button type="button" data-bs-dismiss="offcanvas" class="border-dark-subtle btn-sm btn btn-light me-2 ms-2">
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { Promotion } from '@/services/endpoint/promotion.service';
import Select2 from 'vue3-select2-component';
import Popper from "vue3-popper";
import DateRangePicker from 'vue3-daterange-picker';
import { VueEditor } from "vue3-editor";
import draggable from 'vuedraggable';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: { $lazy: true },
  validations() { return validation(this.form); },
  props: {
    type: { type: String, default: 'CREATE' },
    promotionId: { type: Number, default: 0 }
  },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      typeOptions: [],
      productOptions: [],
      discountProductOptions: [],
      categoryOptions: [],
      customerGroupOptions: [],
      errors: {},
      oldParams: {},
      form: {
        name: '',
        description: '',
        type: 'tiered_reward',
        priority: 0,
        is_active: true,
        condition_require_member: false,
        condition_customer_group_id: '',
        quota_per_user_per_day: 0,
        daily_limit_total: 0,
        tier_mode: 'single',
        base_after_discount: false,
        base_include_vat: true,
        show_reward_stock_early: true,
        startRange: { startDate: null, endDate: null },
        endRange: { startDate: null, endDate: null },
        items: []
      },
      editor: {
        toolbar: [
          [{
            header: [false, 1, 2, 3, 4, 5, 6]
          }],
          ["bold", "italic", "underline", "strike"],
          [{
            align: ""
          }, {
            align: "center"
          }, {
            align: "right"
          }, {
            align: "justify"
          }],
          [{
            list: "ordered"
          }, {
            list: "bullet"
          }],
          [{
            color: []
          }],
        ],
      },
    };
  },
  components: { PlaceholderContent, AccessDenied, Ladda, Select2, Popper, DateRangePicker, VueEditor, draggable },
  watch: {
    // item_discount ต้องเจาะจง product/category (ไม่มี scope=bill) → ดัน bill→product เมื่อสลับ type
    'form.type'(val) {
      if (val === 'item_discount') {
        this.form.items.forEach((t) => { if (t.scope === 'bill') t.scope = 'product'; });
      }
    },
  },
  computed: {
    typeHint() {
      const t = this.typeOptions.find((o) => o.value === this.form.type);
      return t ? this.$t('_promotion.' + t.hint) : '';
    },
    dateRangeLocale() {
      return {
        firstDay: 1,
        format: 'yyyy-mm-dd',
        applyLabel: this.$t('Apply'),
        cancelLabel: this.$t('Cancel'),
        clearable: true,
        monthNames: this.$storeIndex.monthNames(),
        daysOfWeek: this.$storeIndex.daysOfWeek()
      };
    }
  },
  methods: {
    /**
     * สรุปกฎ 1 ใบเป็นข้อความสั้น ๆ ไว้โชว์บนหัวการ์ด (เช่น "ลด 20% · ตามสินค้า")
     * โปรหนึ่งตัวมีได้หลายกฎ ถ้าไม่มีสรุปต้องกางอ่านทีละใบถึงจะแยกออกว่าใบไหนคือใบไหน
     * คืนค่าว่างเมื่อยังกรอกไม่พอจะสรุปได้ — จะได้ไม่ขึ้นป้ายเปล่า ๆ ตอนเพิ่งกด "เพิ่ม"
     */
    tierSummary(tier) {
      const scope = tier.scope === 'product'
        ? this.$t('_promotion.scope_product')
        : tier.scope === 'category'
          ? this.$t('_promotion.scope_category')
          : this.$t('_promotion.scope_bill');

      if (this.form.type === 'item_discount') {
        const v = Number(tier.discount_value);
        if (!v) return '';
        const text = tier.discount_mode === 'percent'
          ? `${this.$t('_promotion.discount_percent')} ${v}%`
          : tier.discount_mode === 'fixed_price'
            ? `${this.$t('_promotion.New Price')} ${v} ฿`
            : `${this.$t('_promotion.discount_amount')} ${v} ฿`;
        return `${text} · ${scope}`;
      }

      const threshold = tier.condition_type === 'quantity' ? tier.buy_quantity : tier.minimum_spend;
      if (!Number(threshold)) return '';
      const cond = tier.condition_type === 'quantity'
        ? `${this.$t('_promotion.Buy Quantity')} ${threshold}`
        : `${this.$t('_promotion.Minimum Spend')} ${threshold} ฿`;
      const reward = tier.action_type === 'bundle_fixed_price'
        ? this.$t('_promotion.reward_pwp')
        : this.$t('_promotion.reward_free');
      return `${cond} · ${reward}`;
    },
    formatDate(d) {
      if (!d) return '';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return String(d).slice(0, 10);
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${dt.getFullYear()}-${m}-${day}`;
    },
    serverError(field) {
      return this.errors?.[field]?.length && this.oldParams[field] == this.form[field];
    },
    serverErrors(field) {
      return this.oldParams[field] == this.form[field] ? (this.errors?.[field] || []) : [];
    },
    // ข้อความ error ของ field ใน tier แถวที่ idx (จาก vuelidate forEach)
    /**
     * ข้อความ error ของกฎแถวที่ idx — เงียบไว้จนกว่าผู้ใช้จะแตะแถวนั้นสักช่อง
     *
     * ⚠️ `$each.$response.$errors` ของ vuelidate เป็นผลตรวจ "ดิบ" ไม่ผูกกับ $dirty/$touch
     *    เหมือน field ปกติ — พอกด "เพิ่ม" ได้แถวว่าง (reward_product_id = '') กฎ required
     *    จะ fail ทันที แล้วขึ้นแดงตั้งแต่ยังไม่ได้แตะอะไรเลย
     *
     * ⚠️ ห้ามหน่วงจนถึงตอนกดบันทึก เพราะปุ่มบันทึก disabled อยู่ตอนฟอร์ม invalid
     *    ผู้ใช้จะกดไม่ได้และไม่มีอะไรบอกว่าติดตรงไหน → ต้องโชว์ทันทีที่เริ่มกรอกแถวนั้น
     */
    tierError(idx, field) {
      if (this.form.items?.[idx]?._new) return '';
      const each = this.v$?.form?.items?.$each;
      const errs = each?.$response?.$errors?.[idx]?.[field];
      return (errs && errs.length) ? errs[0].$message : '';
    },
    addTier() {
      this.form.items.push({
        _uid: 'new-' + Math.random().toString(36).slice(2),
        // แถวที่เพิ่งเพิ่ม ยังไม่ต้องขึ้นแดง — ล้างธงทันทีที่ผู้ใช้แตะช่องแรกในแถวนี้
        _new: true,
        condition_type: 'spend',
        minimum_spend: 0,
        buy_quantity: 0,
        /**
         * ⚠️ ค่าตั้งต้นต้องเป็นตัวเลือกที่ "มีอยู่จริง" ในรายการของโปรประเภทนั้น
         *
         * ตัวเลือก "ทั้งบิล" ถูก v-if ซ่อนไว้เมื่อเป็นโปรลดราคาสินค้า — ถ้าตั้ง 'bill'
         * ทิ้งไว้ select จะไม่มี option ตรงกับ model แล้วเบราว์เซอร์โชว์ option แรก
         * ("ตามหมวด") ทั้งที่ค่าจริงยังเป็น 'bill' → ผู้ใช้กดเลือก "ตามหมวด" ซ้ำ
         * ไม่เกิด change event เพราะหน้าจอแสดงค่านั้นอยู่แล้ว ต้องกดสลับไปมา 2 ครั้ง
         * ถึงจะติด (watcher 'form.type' แก้ให้เฉพาะแถวเก่า ไม่ครอบแถวที่เพิ่งเพิ่ม)
         */
        scope: this.form.type === 'item_discount' ? 'product' : 'bill',
        product_id: '',
        product_category_id: '',
        action_type: 'free_item',
        reward_product_id: '',
        reward_price: '',
        discount_mode: 'fixed_price',
        discount_value: '',
        free_quantity: 1,
        quota_per_user_per_day: null,
        daily_limit_total: null
      });
    },
    setOptions(products, categories, customerGroups, discountProducts) {
      this.productOptions = (products || []).map(p => ({ id: p.id, text: this.$ln(p) }));
      this.discountProductOptions = (discountProducts || []).map(p => ({ id: p.id, text: this.$ln(p) }));
      this.categoryOptions = (categories || []).map(c => ({ id: c.id, text: this.$ln(c) }));
      this.customerGroupOptions = (customerGroups || []).map(g => ({ id: g.id, text: g.name }));
    },
    // map server tier → form row (derive scope from product_id / product_category_id)
    mapTierFromApi(t) {
      let scope = 'bill';
      if (t.product_id) scope = 'product';
      else if (t.product_category_id) scope = 'category';
      return {
        _uid: t.id ? 'id-' + t.id : 'new-' + Math.random().toString(36).slice(2),
        condition_type: t.condition_type || 'spend',
        minimum_spend: t.minimum_spend ?? 0,
        buy_quantity: parseInt(t.buy_quantity) || 0,
        scope,
        product_id: t.product_id ?? '',
        product_category_id: t.product_category_id ?? '',
        action_type: t.action_type === 'bundle_fixed_price' ? 'bundle_fixed_price' : 'free_item',
        reward_product_id: t.reward_product_id ?? '',
        reward_price: t.reward_price ?? '',
        discount_mode: t.discount_mode ?? 'fixed_price',
        discount_value: t.discount_value ?? '',
        free_quantity: parseInt(t.free_quantity) || 1,
        quota_per_user_per_day: parseInt(t.quota_per_user_per_day) || 0,
        daily_limit_total: parseInt(t.daily_limit_total) || 0
      };
    },
    async getCreateData() {
        console.log('aaa', 'aaa');
      await Promotion.getCreate().then(res => {
        
        const data = res.data?.data ?? res.data ?? {};
        this.typeOptions = data.types || [];
        this.setOptions(data.products, data.categories, data.customer_groups, data.discount_products);
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    async getUpdateData() {
      await Promotion.getUpdate(this.promotionId).then(res => {
        const payload = res.data?.data;
        const data = payload?.data ?? payload;
        const filter = res.data?.filter ?? payload?.filter ?? {};
        this.typeOptions = filter.types || data.types || [];
        this.setOptions(filter.products || data.products || [], filter.categories || data.categories || [], filter.customer_groups || data.customer_groups || [], filter.discount_products || data.discount_products || []);

        this.form.name = data.name || '';
        this.form.description = data.description || '';
        this.form.type = data.type || 'tiered_reward';
        this.form.priority = parseInt(data.priority) || 0;
        this.form.is_active = !!data.is_active;
        this.form.condition_require_member = !!data.condition_require_member;
        this.form.condition_customer_group_id = data.condition_customer_group_id ?? '';
        this.form.quota_per_user_per_day = parseInt(data.quota_per_user_per_day) || 0;
        this.form.daily_limit_total = parseInt(data.daily_limit_total) || 0;
        this.form.tier_mode = data.tier_mode || 'single';
        this.form.base_after_discount = !!data.base_after_discount;
        this.form.base_include_vat = data.base_include_vat === undefined ? true : !!data.base_include_vat;
        this.form.show_reward_stock_early = data.show_reward_stock_early === undefined ? true : !!data.show_reward_stock_early;
        const sd = data.start_date ? String(data.start_date).slice(0, 10) : null;
        const ed = data.end_date ? String(data.end_date).slice(0, 10) : null;
        this.form.startRange = { startDate: sd, endDate: sd };
        this.form.endRange = { startDate: ed, endDate: ed };
        this.form.items = (data.items || []).map(this.mapTierFromApi);
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    setParamsBeforeSend() {
      const f = _.cloneDeep(this.form);
      const params = {
        name: f.name,
        description: f.description || undefined,
        type: f.type,
        priority: parseInt(f.priority) || 0,
        is_active: f.is_active ? 1 : 0,
        condition_require_member: f.condition_require_member ? 1 : 0,
        // group/quota เฉพาะตอน require_member (per-user quota + กลุ่ม ต้องเป็นสมาชิก)
        condition_customer_group_id: (f.condition_require_member && f.condition_customer_group_id) ? f.condition_customer_group_id : null,
        quota_per_user_per_day: f.condition_require_member ? (parseInt(f.quota_per_user_per_day) || 0) : 0,
        daily_limit_total: f.condition_require_member ? (parseInt(f.daily_limit_total) || 0) : 0,
        tier_mode: f.tier_mode,
        base_after_discount: f.base_after_discount ? 1 : 0,
        base_include_vat: f.base_include_vat ? 1 : 0,
        show_reward_stock_early: f.show_reward_stock_early ? 1 : 0,
      };
      if (f.startRange && f.startRange.startDate) params.start_date = this.formatDate(f.startRange.startDate) + ' 00:00:00';
      if (f.endRange && f.endRange.startDate) params.end_date = this.formatDate(f.endRange.startDate) + ' 23:59:59';

      const isItemDiscount = f.type === 'item_discount';
      params.items = f.items.map((t, i) => {
        const item = {
          sort_order: i,
          // item_discount ไม่มี threshold/reward → ส่งค่า default ให้ BE (action_type='discount', condition default)
          condition_type: isItemDiscount ? 'quantity' : t.condition_type,
          action_type: isItemDiscount ? 'discount' : t.action_type,
          minimum_spend: (!isItemDiscount && t.condition_type === 'spend') ? (parseFloat(t.minimum_spend) || 0) : 0,
          buy_quantity: (!isItemDiscount && t.condition_type === 'quantity') ? (parseInt(t.buy_quantity) || 0) : 0,
          free_quantity: isItemDiscount ? 1 : (parseInt(t.free_quantity) || 1),
          // scope → only one of product_id / product_category_id
          product_id: t.scope === 'product' ? (t.product_id || null) : null,
          product_category_id: t.scope === 'category' ? (t.product_category_id || null) : null,
          reward_product_id: isItemDiscount ? null : (t.reward_product_id || null),
          quota_per_user_per_day: parseInt(t.quota_per_user_per_day) || 0,
          daily_limit_total: parseInt(t.daily_limit_total) || 0,
        };
        if (isItemDiscount) {
          item.discount_mode = t.discount_mode || 'fixed_price';
          item.discount_value = (t.discount_value === '' || t.discount_value === null) ? null : parseFloat(t.discount_value);
        } else if (t.action_type === 'bundle_fixed_price') {
          item.reward_price = t.reward_price === '' || t.reward_price === null ? null : parseFloat(t.reward_price);
        }
        return item;
      });
      return params;
    },
    async handleCreate() {
      this.loadingSave = true;
      // กดบันทึกแล้วต้องเห็น error ครบทุกแถว รวมแถวที่ยังไม่เคยแตะ (ดู tierError)
      this.form.items.forEach(t => { t._new = false; });
      this.v$.$touch();
      if (this.v$.$invalid) { this.loadingSave = false; return; }
      try {
        await Promotion.create(this.setParamsBeforeSend());
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('_promotion.Save Promotion Success!'));
        $(this.$refs.offcanvas).offcanvas('hide');
      } catch (err) {
        if (err?.response?.data?.errors) {
          this.oldParams = _.clone(this.form);
          this.errors = err.response.data.errors;
        } else {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      } finally {
        this.loadingSave = false;
      }
    },
    async handleUpdate() {
      this.loadingSave = true;
      // กดบันทึกแล้วต้องเห็น error ครบทุกแถว รวมแถวที่ยังไม่เคยแตะ (ดู tierError)
      this.form.items.forEach(t => { t._new = false; });
      this.v$.$touch();
      if (this.v$.$invalid) { this.loadingSave = false; return; }
      try {
        await Promotion.update(this.promotionId, this.setParamsBeforeSend());
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('_promotion.Update Promotion Success!'));
        $(this.$refs.offcanvas).offcanvas('hide');
      } catch (err) {
        if (err?.response?.data?.errors) {
          this.oldParams = _.clone(this.form);
          this.errors = err.response.data.errors;
        } else {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      } finally {
        this.loadingSave = false;
      }
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('shown.bs.offcanvas', async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
        console.error(err);
        this.loading = false;
      }
    });
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  }
};
</script>

<style scoped>
/*
  select2 ตั้งต้นสูง ~38px ส่วนช่องอื่นในการ์ดนี้เป็น form-select-sm (~31px)
  วางเรียงกันแล้วสูงไม่เท่า เส้นขอบคนละสี ดูเหมือนคนละฟอร์ม — บีบให้เข้าชุดกัน
  (แนวเดียวกับที่ inventory-production-batches ทำไว้)
*/
:deep(.select2-container) {
  width: 100% !important;
}
:deep(.select2-container--default .select2-selection--single) {
  height: 31px !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 0.25rem !important;
}
:deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
  line-height: 29px !important;
  padding-left: 0.5rem !important;
  padding-right: 1.75rem !important;
  font-size: 0.875rem;
}
:deep(.select2-container--default .select2-selection--single .select2-selection__arrow) {
  height: 29px !important;
}
:deep(.select2-container--default .select2-selection--single .select2-selection__placeholder) {
  color: #8a929b !important;
}
:deep(.select2-container--open .select2-selection--single) {
  border-color: #E96C15 !important;
}
.drag-handle {
  color: #c4c9cf;
  transition: color 0.15s ease;
  user-select: none;
}
.drag-handle:hover {
  color: #E96C15;
}
.drag-handle:active {
  cursor: grabbing !important;
}
.drag-ghost {
  opacity: 0.5;
}
.sortable-chosen .drag-handle {
  color: #E96C15;
}
</style>
